import {AfterViewInit, ChangeDetectorRef, Component, inject, model, OnInit, signal, ViewChild} from '@angular/core';
import {
  ButtonComponent,
  ButtonOptions,
  DateComponent, DateOptions, FormComponent,
  InputComponent, InputOptions,
  ModalComponent,
  ModalOptions
} from "@shared/components";
import {DropdownComponent, DropdownOptions} from "@shared/components/dropdown/dropdown.component";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  NgForm,
  ReactiveFormsModule, ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {NgOptimizedImage} from "@angular/common";
import {assets, FILTER_ONLY_NUMBERS, FILTER_OTP_NUMBERS} from '@core/constants';
import {Coding} from '@core/classes/other/coding';
import {APIService, DataService, ToastService} from '@core/services';
import {PatientService} from '@core/services/patient.service';
import {HTTPMethods, InputModes, InputTypes, ModalTypes} from '@core/enums';
import {ATC} from '@core/classes/other/atc';
import {HttpHeaders} from '@angular/common/http';
import {DefaultSearchComponent} from '@shared/components/dropdown/search/default-search/default-search.component';
import {lastValueFrom} from 'rxjs';
import {Response, ResponseSingle} from '@core/classes/other/response';
import {PhysicalFinding} from '@core/classes/tables/physical-finding';
import {VitalSign} from '@core/classes/other/vital-sign';
import moment from 'moment/moment';
import {checkDate} from '@core/helpers';

@Component({
  selector: 'ncp-add-physical-finding',
  imports: [
    ButtonComponent,
    DateComponent,
    FormsModule,
    ModalComponent,
    NgOptimizedImage,
    InputComponent,
    ReactiveFormsModule
  ],
  templateUrl: './add-physical-finding.component.html',
  styleUrl: './add-physical-finding.component.scss',
})
export class AddPhysicalFindingComponent implements OnInit, AfterViewInit {
  @ViewChild('mod') public mod!: ModalComponent;
  @ViewChild('pfFrm') protected physicalFindingsFormEl?: NgForm;
  @ViewChild('addPhysicalFindingButton') public addPhysicalFindingButton!: ButtonComponent;

  @ViewChild('systolic') public systolic!: InputComponent;
  @ViewChild('diastolic') public diastolic!: InputComponent;
  @ViewChild('measurementDate') public measurementDate!: DateComponent;

  protected readonly toastService = inject(ToastService);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private apiService = inject(APIService);
  private patientService = inject(PatientService);
  private dataService = inject(DataService);

  protected data = model<any>();

  private _params = {};
  protected form: FormGroup = new FormGroup({
    systolic: new FormControl<number | undefined>(undefined, [Validators.required, Validators.min(1)]),
    diastolic: new FormControl<number | undefined>(undefined, [Validators.required, Validators.min(1)]),
    date: new FormControl<string>('', [Validators.required]),
  }, {
    validators: [this.formValidator()]
  });

  formValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let errors: ValidationErrors | null = {};

      let systolic = (control as FormGroup).controls['systolic'];
      let diastolic = (control as FormGroup).controls['diastolic'];
      let date = (control as FormGroup).controls['date'];

      let patientDOB = moment(this.patientService.data?.dob, ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY.MM.DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY'], true);
      let today = moment();

      if(systolic.hasValidator(Validators.required) && (systolic.value === undefined || systolic.value === null)) {
        systolic.setErrors({pf_systolicInvalid: true});
        errors['pf_systolicInvalid'] = true;
      }

      if(diastolic.hasValidator(Validators.required) && (diastolic.value === undefined || diastolic.value === null)) {
        diastolic.setErrors({pf_diastolicInvalid: true});
        errors['pf_diastolicInvalid'] = true;
      }

      if(systolic.valid && diastolic.valid && parseInt(systolic.value) < parseInt(diastolic.value)) {
        systolic.setErrors({pf_systolicLessThanDiastolic: true});
        diastolic.setErrors({pf_diastolicMoreThanSystolic: true});
        errors['pf_systolicLessThanDiastolic'] = true;
      }

      if(!checkDate(date.value).isValid()) {
        date.setErrors({pi_onSetDateFormatInvalid: true});
        errors['pf_measurementDateInvalid'] = true;
      } else {
        if(checkDate(date.value).isBefore(patientDOB)) {
          date.setErrors({da_onSetDateAfterDOB: true});
          errors['pf_measurementDateAfterDOB'] = true;
        } else if(checkDate(date.value).isSameOrAfter(today)) {
          date.setErrors({da_onSetDateBeforeToday: true});
          errors['pf_measurementDateBeforeToday'] = true;
        } else {
          date.setErrors(null);
        }
      }

      return Object.keys(errors).length > 0 ? errors : null;
    }
  }

  public btnPFOptions = signal<ButtonOptions>(
    new ButtonOptions({
      id: 'pfBtn',
      tooltip: 'add_physical_finding',
    })
  );

  protected modOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'modal',
      title: 'add_physical_finding',
      type: ModalTypes.form,
      form: FormComponent,
      options: {
        animation: true,
        size: 'xl',
        fullscreen: "lg",
        backdrop: 'static',
        keyboard: false,
      },
      onClose: () => {this.onFormReset()}
    })
  );

  protected systolicOptions = model<InputOptions>(
    new InputOptions({
      id: 'systolic_bp',
      label: {
        text: 'systolic_bp',
      },
      type: InputTypes.text,
      mode: InputModes.numeric,
      name: 'systolic_bp',
      formControl: (this.form.get('systolic') as FormControl),
      initialValue: '',
      required: true
    })
  );

  protected diastolicOptions = model<InputOptions>(
    new InputOptions({
      id: 'diastolic_bp',
      label: {
        text: 'diastolic_bp',
      },
      type: InputTypes.text,
      mode: InputModes.numeric,
      name: 'diastolic_bp',
      formControl: (this.form.get('diastolic') as FormControl),
      initialValue: '',
      required: true
    })
  );

  protected onNumericInput = (input: HTMLInputElement): any => {
    return input.value.replace(FILTER_ONLY_NUMBERS, '');
  };

  protected measurementDateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'measurementDate',
      label: {
        text: 'bp_measurement_date',
      },
      name: 'measurementDate',
      initialValue: '',
      required: true,
      minDate: this.patientService.patient?.data?.dob,
      maxDate: moment().format('YYYY-MM-DD'),
    })
  );

  ngOnInit() {
    this.btnPFOptions().onClick = (event) => {
      this.mod.options().onReset = this.onFormReset;
      this.addPhysicalFindingButton.blur();
      this.mod.open();
    };

    this.measurementDateOptions().formControl = (this.form.get('date') as FormControl);
    this.systolicOptions().onInput = this.onNumericInput;
    this.diastolicOptions().onInput = this.onNumericInput;
    this.modOptions().onSave = this.onSave;
  }

  onFormReset = () => {
    console.log('on reset form');
    this.form.reset();
    this.systolic.reset();
    this.diastolic.reset();
    this.measurementDate.reset();

    this.modOptions().errors = [];
  }

  ngAfterViewInit(): void {

  }

  onSave = () => {
    this.physicalFindingsFormEl?.ngSubmit.emit();
  }

  validate = (): boolean => {
    Object.keys(this.form.controls).forEach((control) => {
      this.form.controls[control].markAsTouched();
    })

    return true;
  }

  onFormSubmit = () => {
    console.log(this.form.value);
    if(this.validate() && this.form.invalid) {
      console.log('this.form.is.invalid');
      this.modOptions().errors = this.form.errors!;
      this.mod.detectChanges();
      return;
    }

    this.modOptions().disableOnSave = true;
    let bodySystolic = {
      date: (this.form.get('date') as FormControl).value,
      coding_id: 6529,
      unit_id: 23128,
      status: 1,
      value: (this.form.get('systolic') as FormControl).value,
      version_id: this.patientService.version!.new,
    }

    let bodyDiastolic = {
      date: (this.form.get('date') as FormControl).value,
      coding_id: 6528,
      unit_id: 23128,
      status: 1,
      value: (this.form.get('diastolic') as FormControl).value,
      version_id: this.patientService.version!.new,
    }

    this.apiService.callMany(
      [
        {
          path: '/vital-sign',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          type: HTTPMethods.POST,
          body: bodySystolic,
          name: 'systolic'
        },
        {
          path: '/vital-sign',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          type: HTTPMethods.POST,
          body: bodyDiastolic,
          name: 'diastolic'
        }
      ]
    )?.subscribe(res => {
      if (res && res['systolic'] && res['diastolic']) {
        let dataSystolic = res['systolic'] as ResponseSingle<VitalSign>;
        let dataDiastolic = res['diastolic'] as ResponseSingle<VitalSign>;
        if ((dataSystolic.status?.toString() === '1') && (dataDiastolic.status?.toString() === '1')) {
          let data = this.data()?.tblOptions().data

          let body = {
            date: dataSystolic.data?.date,
            diastolic_coding_id: dataDiastolic.data?.coding_id,
            firstname: dataSystolic.data?.firstname,
            version_id: dataSystolic.data?.version_id,
            lastname: dataSystolic.data?.lastname,
            dt: dataSystolic.data?.dt,
            diastolic: dataDiastolic.data?.value,
            systolic: dataSystolic.data?.value,
            user_id: dataSystolic.data?.user_id,
            patient_id: dataSystolic.data?.patient_id,
            systolic_coding_id: dataSystolic.data?.coding_id,
            version_status: dataSystolic.data?.version_status,
            status: dataSystolic.data?.status,
          };
          data.unshift(body);
          if (this.data && this.data() && this.data().tbl && this.data().tbl.tableData) {
            this.data()!.tbl!.tableData! = data;
          }
          //this.data()?.tbl?.detectChanges();
          this.mod.close();
        }
      }
    })
  }

  protected readonly assets = assets;
}
